import React from "react";
import { CaretLeft } from "@phosphor-icons/react";
import { CONSENT_DETAILS } from "components/common/consent/consentDetails";
import { useLocale } from "lib/hooks/useLocale";
import { Consent } from "lib/types/consent";

// Consents without placeholder copy yet.
const FALLBACK_PARAGRAPHS = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante venenatis dapibus posuere velit aliquet. Sed posuere consectetur est at lobortis. Cras mattis consectetur purus sit amet fermentum.",
  "Aenean lacinia bibendum nulla sed consectetur. Donec ullamcorper nulla non metus auctor fringilla. Maecenas faucibus mollis interdum. Nullam quis risus eget urna mollis ornare vel eu leo. Vestibulum id ligula porta felis euismod semper.",
];

interface ConsentDetailContentProps {
  consent: Consent;
  onBack: () => void;
  /**
   * Classes for the container, mainly its fixed height: the header stays put and
   * the statement scrolls below it. The default is taller than Manage preferences
   * (capped at 80vh) so it visibly stacks over it, as in the design.
   */
  className?: string;
}

const ConsentDetailContent: React.FC<ConsentDetailContentProps> = ({
  consent,
  onBack,
  className = "h-[92vh] max-h-[640px]",
}) => {
  const { t } = useLocale();
  const detail = CONSENT_DETAILS[consent.code];
  const sections = detail?.sections ?? [
    { heading: t("consent_detailed_statement"), paragraphs: FALLBACK_PARAGRAPHS },
  ];

  return (
    <div className={`flex w-full flex-col overflow-hidden rounded-t-3xl bg-white ${className}`}>
      <div className="flex w-full shrink-0 items-center gap-3 bg-white px-5">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black/[0.04]">
          <CaretLeft size={22} className="text-[#18181b]" />
        </button>
        <h2 className="flex-1 py-4 text-base font-semibold leading-6 tracking-[-0.16px] text-[#18181b]">
          {consent.title}
        </h2>
      </div>

      <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto pb-6">
        {(detail?.intro ?? consent.description) && (
          <p className="px-6 text-xs leading-[18px] tracking-[-0.12px] text-[#27272a]/70">
            {detail?.intro ?? consent.description}
          </p>
        )}

        {sections.map(({ heading, paragraphs }) => (
          <div key={heading} className="flex flex-col gap-1 px-6 pt-3">
            <p className="text-sm font-semibold leading-[22px] tracking-[-0.14px] text-[#27272a]">
              {heading}
            </p>
            {paragraphs.map((paragraph) => (
              <p key={paragraph} className="mb-3 text-xs leading-[18px] tracking-[-0.12px] text-[#27272a]/70">
                {paragraph}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConsentDetailContent;
